import { useMemo } from "react";
import { Sparkles } from "lucide-react";
import type { Agent } from "@/types/aiToolkit";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { computeAgentStrength } from "@/lib/computeAgentStrength";

export interface AgentStrengthMeterProps {
  value: Partial<Agent>;
  onNudgeClick?: (sectionId: string) => void;
  className?: string;
}

export function AgentStrengthMeter({
  value,
  onNudgeClick,
  className,
}: AgentStrengthMeterProps) {
  const { score, nudges } = useMemo(() => computeAgentStrength(value), [value]);
  const topNudges = nudges.slice(0, 2);

  const strengthLabel =
    score >= 80 ? "Strong" : score >= 50 ? "Developing" : score >= 25 ? "Basic" : "Getting started";

  return (
    <div
      className={`rounded-lg border bg-muted/30 p-3 space-y-2 ${className ?? ""}`}
      data-testid="agent-strength-meter"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium truncate">Agent Strength</span>
          <Badge variant="secondary" className="shrink-0 text-xs">
            {strengthLabel}
          </Badge>
        </div>
        <span className="text-sm font-semibold tabular-nums shrink-0">{score}%</span>
      </div>
      <Progress value={score} className="h-2" />
      {topNudges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {topNudges.map((nudge) => (
            <button
              key={nudge.id}
              type="button"
              className="text-xs text-left text-muted-foreground hover:text-primary underline-offset-2 hover:underline transition-colors"
              onClick={() => onNudgeClick?.(nudge.sectionId)}
            >
              {nudge.label} (+{nudge.weight}%)
            </button>
          ))}
        </div>
      )}
      {score >= 100 && (
        <p className="text-xs text-muted-foreground">Your agent is fully configured.</p>
      )}
    </div>
  );
}
